import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { EnvelopeSimple, Lock, ArrowLeft, Eye } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { toast } from 'sonner';

const Login = ({ onSwitchToRegister, onForgotPassword, onBack }) => {
  const navigate = useNavigate();
  const { login, startGuestSession } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);

  const handleGuestPreview = async () => {
    setGuestLoading(true);
    try {
      await startGuestSession();
      toast.success('Welcome! Exploring as guest 👋');
      navigate('/dashboard');
    } catch (e) {
      toast.error('Could not start guest session.');
    } finally {
      setGuestLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle different error formats
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (error.response?.data?.detail) {
        // Check if detail is an array (validation errors)
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map(err => err.msg || JSON.stringify(err))
            .join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 noise-bg"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1594886801338-b81548345f77?q=80&w=2000&auto=format&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-black/80" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <div className="glass-card p-8">
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="mb-4 text-zinc-400 hover:text-white"
              data-testid="back-button"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back
            </Button>
          )}

          <div className="text-center mb-8">
            <h1 className="text-3xl font-unbounded font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent mb-2">
              Welcome Back
            </h1>
            <p className="text-zinc-400">Login to your TeenVest account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Email
              </label>
              <div className="relative">
                <EnvelopeSimple size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 bg-black/50 border-white/10 focus:border-gold/50 text-white"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 bg-black/50 border-white/10 focus:border-gold/50 text-white"
                  required
                  data-testid="password-input"
                />
              </div>
              {onForgotPassword && (
                <div className="text-right mt-2">
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    className="text-xs text-zinc-400 hover:text-gold transition-colors font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold py-6 rounded-full hover:scale-105 transition-transform shadow-glow"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          {/* Google sign-in + guest preview */}
          <div className="mt-4">
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-zinc-500 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div className="mb-3">
              <GoogleSignInButton label="Continue with Google" />
            </div>
            <button
              type="button"
              onClick={handleGuestPreview}
              disabled={guestLoading}
              className="group w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-white/5 border border-white/15 text-zinc-200 hover:bg-white/10 hover:border-white/30 transition-all font-medium disabled:opacity-60"
              data-testid="login-guest-preview-btn"
            >
              <Eye size={18} weight="fill" className="text-gold group-hover:scale-110 transition-transform" />
              <span>{guestLoading ? 'Loading preview...' : 'Continue without signing up'}</span>
            </button>
            <p className="text-[11px] text-zinc-500 text-center mt-2">
              Preview only · browse stocks & Learn mode, no trading or game modes
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-zinc-400">
              Don't have an account?{' '}
              <button
                onClick={onSwitchToRegister}
                className="text-gold hover:text-gold-light font-bold transition-colors"
                data-testid="switch-to-register"
              >
                Sign up
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;